import React, { useState, useEffect } from 'react';
import { queryOverrides } from '../../rag/queryOverrides';
import { useHOA } from '../../contexts/HOAContext';
import { MaterialIcon } from '../ui';
import { countOpen } from '../../services/ragReviewService';
import { getPeopleRoster } from '../../services/activityService';
import ReviewQueue from './query/ReviewQueue';

const ROLE_LABELS = { admin: 'Admin', board: 'Board', acc_member: 'ACC', management: 'Management', member: 'Member' };

const QueryConsole = ({
    currentUser, includeAdmin = false, includeArchived = false, personFilter = null, onClearPersonFilter,
}) => {
    const { hoa } = useHOA();
    const [activeTab, setActiveTab] = useState('log');
    const [queryLog, setQueryLog] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [reviewCount, setReviewCount] = useState(0);
    // user_id -> roster row (name, excluded, archived), for asker names and the toggles
    const [peopleById, setPeopleById] = useState(new Map());
    const [searchTerm, setSearchTerm] = useState('');
    const [newOverrideTrigger, setNewOverrideTrigger] = useState('');
    const [newOverrideAnswer, setNewOverrideAnswer] = useState('');
    const [showAddOverride, setShowAddOverride] = useState(false);
    const [viewingThread, setViewingThread] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, hoa?.id]);

    // Arriving from the People view's "AI questions" link: show that person's log.
    useEffect(() => {
        if (personFilter?.id) setActiveTab('log');
    }, [personFilter?.id]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setViewingThread(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const loadData = async () => {
        if (!hoa?.id) {
            setIsLoading(true);
            return;
        }

        setIsLoading(true);
        setLoadError('');
        try {
            const [log, ovr, people] = await Promise.all([
                queryOverrides.getQueryLog(500, hoa.id),
                queryOverrides.getAll(hoa.id),
                getPeopleRoster({ hoaId: hoa.id, includeArchived: true, includeExcluded: true }).then(
                    null,
                    (err) => {
                        console.warn('Asker names could not be loaded:', err?.message || err);
                        return [];
                    }
                ),
            ]);
            setQueryLog(log);
            setOverrides(ovr);
            setPeopleById(new Map(people.filter(p => p.user_id).map(p => [p.user_id, p])));
        } catch (err) {
            setLoadError(err?.message || 'Query activity could not be loaded.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveOverride = async () => {
        if (!newOverrideTrigger.trim() || !newOverrideAnswer.trim()) return;
        await queryOverrides.saveOverride(
            newOverrideTrigger,
            newOverrideAnswer,
            currentUser?.id || 'admin',
            null,
            hoa.id
        );
        setNewOverrideTrigger('');
        setNewOverrideAnswer('');
        setShowAddOverride(false);
        await loadData();
    };

    const refreshReviewCount = React.useCallback(async () => {
        if (!hoa?.id) return;
        try {
            setReviewCount(await countOpen({ hoaId: hoa.id, includeExcluded: includeAdmin, includeArchived }));
        } catch (err) {
            console.warn('Review queue count could not be loaded:', err?.message || err);
        }
    }, [hoa?.id, includeAdmin, includeArchived]);

    useEffect(() => {
        refreshReviewCount();
    }, [refreshReviewCount]);

    // "Create override" from the review queue: open the Overrides form pre-filled.
    const handleCreateOverride = (questionText) => {
        setNewOverrideTrigger(questionText || '');
        setNewOverrideAnswer('');
        setShowAddOverride(true);
        setActiveTab('overrides');
    };

    const handleViewThread = (threadId, logId) => {
        const turns = queryLog
            .filter(q => (q.thread_id || q.id) === (threadId || logId))
            .reverse();
        if (turns.length) setViewingThread({ id: threadId || logId, queries: turns });
    };


    const askerName = (q) => peopleById.get(q.user_id)?.full_name || (q.user_id ? 'Unknown account' : 'Anonymous');

    // "Show admin & test" / "Include archived" apply to the log too.
    const visibleLog = queryLog.filter(q => {
        if (personFilter?.id) return q.user_id === personFilter.id;
        const person = peopleById.get(q.user_id);
        if (!person) return true;
        if (!includeAdmin && person.is_excluded) return false;
        if (!includeArchived && person.account_state === 'archived') return false;
        return true;
    });

    // Group queries into threads
    const threadMap = {};
    const groupedLog = [];

    // queryLog is newest first.
    visibleLog.forEach(q => {
        const tId = q.thread_id || q.id;
        if (!threadMap[tId]) {
            threadMap[tId] = {
                id: tId,
                queries: [], // will fill with oldest-first or newest? 
            };
            groupedLog.push(threadMap[tId]);
        }
        // unshift ensures the oldest query in a thread appears first in the array
        threadMap[tId].queries.unshift(q);
    });

    const filteredLog = groupedLog.filter(thread => {
        if (!searchTerm) return true;
        const term = String(searchTerm).toLowerCase();
        return thread.queries.some(q =>
            String(q.query || '').toLowerCase().includes(term) ||
            String(q.user_role || '').toLowerCase().includes(term) ||
            askerName(q).toLowerCase().includes(term) ||
            String(q.user_id || '').toLowerCase().includes(term)
        );
    });

    const tabs = [
        {
            id: 'log',
            label: 'Query Log',
            materialIcon: 'chat_bubble',
            count: visibleLog.length
        },
        {
            id: 'overrides',
            label: 'Overrides',
            materialIcon: 'edit',
            count: overrides.filter(o => o.active).length
        },
        {
            id: 'review',
            label: 'Needs review',
            materialIcon: 'rate_review',
            count: reviewCount,
            countLabel: reviewCount >= 100 ? '100+' : String(reviewCount),
            alert: reviewCount > 0,
        },
    ];

    return (
        <div className="flex flex-col h-full bg-main-bg">
            {/* Header / Tabs */}
            <div className="px-4 md:px-6 py-4 border-b border-amber-100/40 bg-white z-10 overflow-x-auto">
                <div className="flex gap-2 min-w-max" role="tablist" aria-label="Query Console">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`inline-flex items-center gap-2 px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all border
                                ${activeTab === tab.id
                                    ? 'bg-primary-dark text-white border-primary-dark'
                                    : 'bg-white text-slate-600 border-amber-100/60 hover:border-secondary/40 hover:text-primary-dark'
                                }`}
                        >
                            <MaterialIcon name={tab.materialIcon} size={16} />
                            {tab.label}
                            {tab.count > 0 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold
                                    ${activeTab === tab.id
                                        ? 'bg-white/20 text-white'
                                        : tab.alert
                                            ? 'bg-secondary/15 text-secondary'
                                            : 'bg-section-bg text-slate-600'
                                    }`}>
                                    {tab.countLabel || tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">

                {/* ── QUERY LOG TAB ── */}
                {activeTab === 'log' && (
                    <div>
                        {personFilter?.id && (
                            <div className="mb-3 flex items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-bold">
                                    <MaterialIcon name="person" size={14} />
                                    Questions from {personFilter.name}
                                    <button
                                        type="button"
                                        onClick={onClearPersonFilter}
                                        aria-label="Show everyone's questions"
                                        className="w-6 h-6 rounded-full hover:bg-secondary/20 flex items-center justify-center"
                                    >
                                        <MaterialIcon name="close" size={14} />
                                    </button>
                                </span>
                            </div>
                        )}
                        <div className="relative mb-6">
                            <MaterialIcon name="search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search questions or people..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 text-sm bg-white border border-amber-100/60 rounded-full
                                    focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20 transition-all"
                            />
                        </div>

                        {isLoading ? (
                            <div className="text-center py-12 text-slate-500">
                                <span className="material-symbols-outlined text-3xl text-secondary/60 animate-spin mb-3 block">progress_activity</span>
                                <p className="text-sm font-medium">Loading query activity...</p>
                            </div>
                        ) : loadError ? (
                            <div className="text-center py-12 text-red-600">
                                <MaterialIcon name="warning" size={32} className="block mx-auto mb-2 opacity-60" />
                                <p className="text-sm font-medium">{loadError}</p>
                            </div>
                        ) : filteredLog.length === 0 ? (
                            <div className="text-center py-12 text-slate-400">
                                <MaterialIcon name="chat_bubble" size={32} className="block mx-auto mb-2 opacity-30" />
                                <p className="text-sm">No queries yet.</p>
                                <p className="text-xs mt-1">
                                    Queries appear here once AI search is active.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {filteredLog.map(thread => {
                                    const mainQuery = thread.queries[0];
                                    const followUps = thread.queries.slice(1);

                                    return (
                                        <div key={thread.id}
                                            onClick={() => setViewingThread(thread)}
                                            className="border border-amber-100/40 rounded-2xl overflow-hidden bg-white hover:border-secondary/40 cursor-pointer transition-all">
                                            <div className="p-5 relative">
                                                {/* Thread Header Context / Timestamp row */}
                                                <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                                                    <span className="text-xs text-slate-400 flex items-center gap-1 font-medium">
                                                        <MaterialIcon name="schedule" size={14} />
                                                        {new Date(mainQuery.timestamp).toLocaleDateString()}
                                                    </span>
                                                    <span className="text-sm font-bold text-primary-dark">
                                                        {askerName(mainQuery)}
                                                    </span>
                                                    {mainQuery.user_role && (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 px-2.5 py-0.5 bg-section-bg rounded-full">
                                                            {ROLE_LABELS[mainQuery.user_role] || mainQuery.user_role}
                                                        </span>
                                                    )}
                                                    {mainQuery.override_used && (
                                                        <span className="text-[10px] md:text-xs text-forest-600 bg-forest-50 px-2 py-0.5 rounded-full font-medium">
                                                            Admin override served
                                                        </span>
                                                    )}

                                                    {followUps.length > 0 && (
                                                        <span className="md:ml-auto flex items-center gap-1.5 text-[10px] md:text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 rounded">
                                                            <MaterialIcon name="chat_bubble" size={14} />
                                                            {followUps.length} follow-up{followUps.length > 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Top-Level Query */}
                                                <div className="flex items-start gap-2 mb-2">
                                                    <div className="w-5 h-5 rounded bg-forest-100 flex items-center justify-center shrink-0 mt-0.5">
                                                        <span className="text-[10px] text-forest-700 font-bold">Q</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-slate-800 pt-0.5">
                                                        {mainQuery.query}
                                                    </p>
                                                </div>

                                                {/* Top-Level Answer Limit To Line Clamp */}
                                                {mainQuery.answer && (
                                                    <div className="flex items-start gap-2 mt-2">
                                                        <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                                                            <span className="text-[10px] text-slate-600 font-bold">A</span>
                                                        </div>
                                                        <p className="flex-1 text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-2.5 line-clamp-2">
                                                            {mainQuery.answer}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ── OVERRIDES TAB ── */}
                {activeTab === 'overrides' && (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <p className="text-sm text-slate-500">
                                Override AI answers with verified correct responses.
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowAddOverride(!showAddOverride)}
                                className="text-sm font-semibold bg-forest-700 text-white px-5 py-2.5 
                  rounded-xl shadow-sm border border-forest-800 hover:bg-forest-600 hover:shadow transition-all"
                            >
                                + Add Override
                            </button>
                        </div>

                        {showAddOverride && (
                            <div className="border-2 border-forest-200 rounded-2xl p-6 
                mb-6 bg-forest-50/50 shadow-sm">
                                <p className="text-sm font-medium text-slate-700 mb-3">
                                    New Override
                                </p>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs text-slate-500 mb-1 block">
                                            Trigger phrase (question pattern to match)
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. what are the setback requirements"
                                            value={newOverrideTrigger}
                                            onChange={e =>
                                                setNewOverrideTrigger(e.target.value)}
                                            className="w-full text-sm border border-slate-200 
                        rounded-lg px-3 py-2 focus:outline-none 
                        focus:border-forest-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-slate-500 mb-1 block">
                                            Correct answer
                                        </label>
                                        <textarea
                                            rows={3}
                                            placeholder="Type the verified correct answer..."
                                            value={newOverrideAnswer}
                                            onChange={e =>
                                                setNewOverrideAnswer(e.target.value)}
                                            className="w-full text-sm border border-slate-200 
                        rounded-lg px-3 py-2 focus:outline-none 
                        focus:border-forest-500 resize-none"
                                        />
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <button type="button"
                                            onClick={() => setShowAddOverride(false)}
                                            className="text-sm text-slate-500 px-3 py-1.5">
                                            Cancel
                                        </button>
                                        <button type="button"
                                            onClick={handleSaveOverride}
                                            className="text-sm bg-forest-700 text-white 
                        px-4 py-1.5 rounded-lg hover:bg-forest-600">
                                            Save Override
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {overrides.length === 0 ? (
                            <div className="text-center py-12 text-slate-400">
                                <MaterialIcon name="edit" size={32} className="block mx-auto mb-2 opacity-30" />
                                <p className="text-sm font-medium">No overrides yet.</p>
                                <p className="text-xs mt-1">
                                    Add overrides to correct AI answers for
                                    specific questions.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {overrides.map(override => (
                                    <div key={override.id}
                                        className={`border-2 rounded-2xl p-5 transition-all shadow-sm
                      ${override.active
                                                ? 'border-slate-200 bg-white hover:border-slate-300 hover:shadow'
                                                : 'border-slate-100 bg-slate-50/80 opacity-60 hover:opacity-100'
                                            }`}>
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-slate-400 mb-1">
                                                    Trigger
                                                </p>
                                                <p className="text-sm font-medium text-slate-700">
                                                    {override.trigger_phrase}
                                                </p>
                                                <p className="text-xs text-slate-400 
                          mt-2 mb-1">
                                                    Override answer
                                                </p>
                                                <p className="text-sm text-slate-600 
                          bg-slate-50 rounded-lg p-2 text-xs">
                                                    {override.correct_answer}
                                                </p>
                                                <div className="flex gap-3 mt-2 text-xs 
                          text-slate-400">
                                                    <span>Used {override.use_count || 0}×</span>
                                                    <span>
                                                        {new Date(override.created_at)
                                                            .toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 ml-3">
                                                <button type="button"
                                                    onClick={async () => {
                                                        await queryOverrides.toggleActive(override.id);
                                                        await loadData();
                                                    }}
                                                    className="text-slate-400 hover:text-forest-600 
                            transition-colors"
                                                    title={override.active
                                                        ? 'Deactivate' : 'Activate'}>
                                                    {override.active
                                                        ? <MaterialIcon name="toggle_on" size={20} className="text-forest-600" />
                                                        : <MaterialIcon name="toggle_off" size={20} />
                                                    }
                                                </button>
                                                <button type="button"
                                                    onClick={async () => {
                                                        await queryOverrides.deleteOverride(override.id);
                                                        await loadData();
                                                    }}
                                                    className="text-slate-400 hover:text-red-500 
                            transition-colors">
                                                    <MaterialIcon name="delete" size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ── NEEDS REVIEW TAB ── */}
                {activeTab === 'review' && (
                    <ReviewQueue
                        hoaId={hoa?.id}
                        includeAdmin={includeAdmin}
                        includeArchived={includeArchived}
                        onCreateOverride={handleCreateOverride}
                        onViewThread={handleViewThread}
                        onChanged={refreshReviewCount}
                    />
                )}
            </div>
            {/* Thread Details Modal */}
            {viewingThread && (
                <div onClick={() => setViewingThread(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
                            <div>
                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                    <MaterialIcon name="chat_bubble" size={20} className="text-forest-600" />
                                    Conversation Thread
                                </h3>
                                <p className="text-xs text-slate-500 mt-1">
                                    {askerName(viewingThread.queries[0])} · started {new Date(viewingThread.queries[0].timestamp).toLocaleString()}
                                </p>
                            </div>
                            <button
                                onClick={() => setViewingThread(null)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <MaterialIcon name="close" size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-6">
                            {viewingThread.queries.map((q, idx) => (
                                <div key={q.id} className={`${idx > 0 && 'border-t border-slate-100 pt-6'}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-xs text-slate-400 flex items-center gap-1 font-medium bg-slate-100 px-2 py-0.5 rounded">
                                            Turn {idx + 1}
                                        </span>
                                        <span className="text-xs text-slate-400 flex items-center gap-1">
                                            <MaterialIcon name="schedule" size={14} />
                                            {new Date(q.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                        {q.override_used && (
                                            <span className="text-[10px] text-forest-600 bg-forest-50 px-2 py-0.5 rounded-full font-medium">
                                                Admin override served
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-6 h-6 rounded-lg bg-forest-100 flex items-center justify-center shrink-0 mt-0.5">
                                            <span className="text-xs text-forest-700 font-bold">Q</span>
                                        </div>
                                        <p className="text-sm font-medium text-slate-800 pt-0.5">
                                            {q.query}
                                        </p>
                                    </div>

                                    {q.answer && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                                                <span className="text-xs text-slate-600 font-bold">A</span>
                                            </div>
                                            <p className="flex-1 text-sm text-slate-600 leading-relaxed bg-slate-50/80 border border-slate-100 rounded-xl p-4">
                                                {q.answer}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QueryConsole;
